// Portal: fleet map. One pin per geocoded ACTIVE site, colored by how
// recently the site showed xsiteio activity (most recent of login /
// price book update / fuel recon): green ≤7 days, amber ≤14, red
// older, grey never. Same Leaflet + OSM setup as the internal SiteMap
// -- no API key. Reads /api/portal/sites (scoped server-side), so a
// customer can only ever map their own fleet.

import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const TONE_COLORS = {
  good:     '#047857',
  warn:     '#b45309',
  bad:      '#b91c1c',
  inactive: '#6b7280',
};

// Most recent activity of any kind, then the table's 7/14-day tones.
function siteTone(s) {
  const times = [s.xsiteio_last_login, s.xsiteio_last_updated, s.xsiteio_last_fuel_recon_at]
    .map((v) => (v ? new Date(v).getTime() : NaN))
    .filter((t) => !Number.isNaN(t));
  if (times.length === 0) return 'inactive';
  const days = (Date.now() - Math.max(...times)) / 86400000;
  if (days <= 7) return 'good';
  if (days <= 14) return 'warn';
  return 'bad';
}

const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function fmtDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  if (!y || !m || !d) return '—';
  return `${MONTHS_SHORT[Number(m) - 1] || m} ${d}, ${y}`;
}

// Colored SVG teardrop pins, cached per color (same trick as the
// internal SiteMap -- avoids Leaflet's default-icon URL issue).
const iconCache = {};
function coloredIcon(color) {
  if (iconCache[color]) return iconCache[color];
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="26" height="34" viewBox="0 0 26 34">
      <path d="M13 0C5.82 0 0 5.82 0 13c0 9.75 13 21 13 21s13-11.25 13-21C26 5.82 20.18 0 13 0z"
            fill="${color}" stroke="#ffffff" stroke-width="1.5"/>
      <circle cx="13" cy="13" r="5" fill="#ffffff"/>
    </svg>`;
  const icon = new L.DivIcon({
    className: 'shi-pin',
    html: svg,
    iconSize:   [26, 34],
    iconAnchor: [13, 34],
    popupAnchor: [0, -32],
  });
  iconCache[color] = icon;
  return icon;
}

function FitToMarkers({ sites }) {
  const map = useMap();
  useEffect(() => {
    if (!sites || sites.length === 0) return;
    const bounds = L.latLngBounds(sites.map((s) => [s.latitude, s.longitude]));
    if (!bounds.isValid()) return;
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 10 });
  }, [sites, map]);
  return null;
}

const DEFAULT_CENTER = [50, -95];
const DEFAULT_ZOOM   = 4;

export default function PortalMap() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [province, setProvince] = useState('');

  useEffect(() => {
    let cancelled = false;
    axios.get('/api/portal/sites')
      .then((res) => { if (!cancelled) setRows(res.data); })
      .catch(() => { if (!cancelled) setError('Failed to load the map. Please try again.'); });
    return () => { cancelled = true; };
  }, []);

  const provinces = useMemo(() => {
    if (!rows) return [];
    return [...new Set(rows.map((s) => s.province_state).filter(Boolean))].sort();
  }, [rows]);

  const mappable = useMemo(() => {
    if (!rows) return [];
    return rows.filter((s) =>
      s.status === 'Active' &&
      s.latitude != null && s.longitude != null &&
      (!province || s.province_state === province)
    );
  }, [rows, province]);

  const counts = useMemo(() => {
    const c = { good: 0, warn: 0, bad: 0, inactive: 0 };
    mappable.forEach((s) => { c[siteTone(s)]++; });
    return c;
  }, [mappable]);

  const unmapped = rows
    ? rows.filter((s) => s.status === 'Active' && (s.latitude == null || s.longitude == null)).length
    : 0;

  return (
    <section className="page">
      <div className="page-header">
        <h2>Fleet Map</h2>
        {rows && (
          <span className="portal-count">
            {mappable.length} site{mappable.length === 1 ? '' : 's'}
            {' · '}
            <span className="portal-dot portal-dot-good" /> {counts.good}
            {' '}<span className="portal-dot portal-dot-warn" /> {counts.warn}
            {' '}<span className="portal-dot portal-dot-bad" /> {counts.bad}
          </span>
        )}
      </div>

      {error && <p className="portal-error">{error}</p>}
      {!rows && !error && <p className="placeholder-note">Loading…</p>}

      {rows && (
        <>
          <div className="portal-toolbar">
            <select
              className="form-input"
              value={province}
              onChange={(e) => setProvince(e.target.value)}
              style={{ maxWidth: 180 }}
              aria-label="Filter by province"
            >
              <option value="">All provinces</option>
              {provinces.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            {unmapped > 0 && (
              <span className="portal-footnote" style={{ margin: 0 }}>
                {unmapped} site{unmapped === 1 ? '' : 's'} not yet mapped
              </span>
            )}
          </div>

          <div className="portal-map-wrap">
            <MapContainer
              center={DEFAULT_CENTER}
              zoom={DEFAULT_ZOOM}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <FitToMarkers sites={mappable} />
              {mappable.map((s) => {
                const tone = siteTone(s);
                return (
                  <Marker
                    key={s.id}
                    position={[s.latitude, s.longitude]}
                    icon={coloredIcon(TONE_COLORS[tone])}
                  >
                    <Popup>
                      <strong>{s.site_name}</strong><br />
                      <span className="mono">{s.site_number}</span><br />
                      {[s.address, s.city, s.province_state].filter(Boolean).join(', ')}<br />
                      Last login: {fmtDate(s.xsiteio_last_login)}<br />
                      Price book: {fmtDate(s.xsiteio_last_updated)}<br />
                      Fuel recon: {fmtDate(s.xsiteio_last_fuel_recon_at)}
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          </div>
          <p className="portal-footnote">
            Pin colors show most recent activity: green = within 7 days,
            amber = within 14 days, red = older.
          </p>
        </>
      )}
    </section>
  );
}
